/**
 * NsectionB15Page
 * B.15.監控參數說明（A6.4-FORM-METH-002 Section 15. Data and parameters monitored）
 * 兩頁籤：15.監控參數(DataTable+Modal) / 15.1.監測報告提交頻率(富文本)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB15Service, { NsectionB15Detail, MonitoringParameterItem } from '../services/nsectionB15Service';
import nsectionB13Service, { ParameterSummaryItem } from '../services/nsectionB13Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const COMMON_UNITS = [
  { label: 'tCO₂e', latex: '\\text{tCO}_2\\text{e}' }, { label: 'MWh', latex: '\\text{MWh}' },
  { label: 'GWh', latex: '\\text{GWh}' }, { label: 'kWh', latex: '\\text{kWh}' },
  { label: 'GJ', latex: '\\text{GJ}' }, { label: 'kg', latex: '\\text{kg}' },
  { label: 't', latex: '\\text{t}' }, { label: '%', latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' }, { label: 'tCO₂e/GJ', latex: '\\text{tCO}_2\\text{e/GJ}' },
];

const EMPTY_PARAM = (): MonitoringParameterItem => ({
  id: crypto.randomUUID(), data_parameter: '', description: '', data_unit: '', equations_referred: '',
  purpose_baseline: false, purpose_project: false, purpose_leakage: false,
  measurement_frequency: '', measurement_methods: '', responsible_entity: '',
  instrument_type: '', accuracy_class: '', calibration_requirements: '', instrument_location: '',
  qaqc_procedures: '', treatment_of_uncertainty: '', additional_comments: '', b13_ref: null,
});

const NsectionB15Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB15Detail | null>(null);
  const [parameters, setParameters] = useState<MonitoringParameterItem[]>([]);
  const [reportingFrequency, setReportingFrequency] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(1);

  const [b13Params, setB13Params] = useState<ParameterSummaryItem[]>([]);
  const [availableParams, setAvailableParams] = useState<ParameterSummaryItem[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalParam, setModalParam] = useState<MonitoringParameterItem>(EMPTY_PARAM());

  useEffect(() => { if (!hasInitialized.current && !permissionLoading) { validateSession('NsectionB15Page'); hasInitialized.current = true; } }, [permissionLoading]);

  const loadB13Params = async () => {
    if (!selectedProposalId) return;
    try {
      const all = await nsectionB13Service.getParametersSummary(selectedProposalId);
      const typeFiltered = all.filter(p => p.para_type === 250 || p.para_type === null);
      const seen = new Set<string>();
      const filtered = typeFiltered.filter(p => { if (seen.has(p.symbol)) return false; seen.add(p.symbol); return true; });
      setB13Params(filtered);
    } catch { setB13Params([]); }
  };

  useEffect(() => {
    if (!selectedProposalId) { setData(null); setParameters([]); setReportingFrequency(''); setB13Params([]); return; }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const r = await nsectionB15Service.getByProposal(selectedProposalId);
        if (r) { setData(r); setParameters(r.parameters || []); setReportingFrequency(r.reporting_frequency || ''); }
        else { setData(null); setParameters([]); setReportingFrequency(''); }
      } catch (err: any) { if (err.response?.status !== 404) setError(err.response?.data?.detail || (isZh ? '載入失敗' : 'Load failed')); }
      finally { setLoading(false); }
    };
    load(); loadB13Params();
  }, [selectedProposalId, isZh]);

  useEffect(() => {
    const built = new Set(parameters.map(p => p.data_parameter));
    setAvailableParams(b13Params.filter(p => !built.has(p.symbol)));
  }, [b13Params, parameters]);

  const builtCount = b13Params.length - availableParams.length;
  const totalCount = b13Params.length;

  const openModal = (index?: number) => {
    if (index !== undefined) { setEditingIndex(index); setModalParam({ ...parameters[index] }); }
    else { setEditingIndex(null); setModalParam(EMPTY_PARAM()); }
    setShowModal(true);
  };

  const handleB13Select = (symbol: string) => {
    const p = b13Params.find(x => x.symbol === symbol);
    if (!p) return;
    setModalParam(prev => ({
      ...prev, data_parameter: p.symbol, data_unit: p.unit || '',
      equations_referred: `${p.source_section} ${p.source_label}`,
      b13_ref: { source_section: p.source_section, source_label: p.source_label, symbol: p.symbol, para_type: p.para_type, equation_latex: p.equation_latex },
    }));
  };

  const handleSaveParam = (continueAdd: boolean) => {
    if (!modalParam.data_parameter.trim()) { toast.warning(isZh ? '請輸入參數名稱' : 'Enter parameter name'); return; }
    const updated = [...parameters];
    if (editingIndex !== null) updated[editingIndex] = modalParam; else updated.push(modalParam);
    setParameters(updated);
    if (continueAdd) { setModalParam(EMPTY_PARAM()); setEditingIndex(null); } else setShowModal(false);
  };

  const handleDeleteParam = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定刪除？' : 'Delete?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const d: any = activeTab === 1 ? { parameters } : { reporting_frequency: reportingFrequency };
      const r = await nsectionB15Service.update(selectedProposalId, d);
      setData(r); toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) { toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Failed')); }
    finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b15', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('nsection_b15', 'update');
  const cs: React.CSSProperties = { width: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px' };
  const tb = (a: boolean): React.CSSProperties => ({ padding: '10px 20px', border: 'none', borderBottom: a ? '3px solid #4A90E2' : '3px solid transparent', background: a ? '#f0f7ff' : 'transparent', color: a ? '#4A90E2' : '#666', fontWeight: a ? 600 : 400, cursor: 'pointer', fontSize: 14, marginBottom: -2 });

  return (
    <div className="page-container">
      <div className="page-header"><FunctionPageHeader funcCode="nsection_b15" /></div>
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 16 }}>{isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}</div>
      ) : (
        <div>
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 16 }}>
            <button onClick={() => setActiveTab(1)} style={tb(activeTab === 1)}>{isZh ? '15. 監控參數' : '15. Monitored parameters'}</button>
            <button onClick={() => setActiveTab(2)} style={tb(activeTab === 2)}>{isZh ? '15.1 監測報告頻率' : '15.1 Reporting frequency'}</button>
          </div>

          {/* Tab 1: 監控參數 */}
          {activeTab === 1 && (
            <div style={cs}>
              {/* 進度列 */}
              <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 8, background: totalCount > 0 && builtCount >= totalCount ? '#f6ffed' : '#f8f9fa', border: `1px solid ${totalCount > 0 && builtCount >= totalCount ? '#b7eb8f' : '#e8e8e8'}` }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {totalCount > 0
                    ? (builtCount >= totalCount
                        ? <span style={{ color: '#52c41a' }}>✅ {isZh ? '所有必要監控參數已建立' : 'All required parameters created'}</span>
                        : <span>{isZh ? `B.13 監控參數：已建立 ${builtCount} / 共 ${totalCount} 個` : `${builtCount} / ${totalCount} created`}</span>)
                    : <span style={{ color: '#999' }}>{isZh ? '尚無參數資料，可手動新增' : 'No parameters, add manually'}</span>}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{isZh ? '監控參數清單' : 'Monitored parameters'}</div>
                {canEdit && <button className="btn-primary" onClick={() => openModal()}>{t('common.create')}</button>}
              </div>

              {parameters.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 14 }}>{isZh ? '尚無參數' : 'No parameters.'}</div>
              ) : (
                <div className="data-table-container"><table className="data-table"><thead className="table-header-dark-green"><tr>
                  <th style={{ width: '4%' }}>#</th>
                  <th style={{ width: '6%' }}>{isZh ? '來源' : 'Src'}</th>
                  <th style={{ width: '12%' }}>{isZh ? '參數' : 'Param'}</th>
                  <th style={{ width: '14%' }}>{isZh ? '說明' : 'Desc'}</th>
                  <th style={{ width: '8%' }}>{isZh ? '單位' : 'Unit'}</th>
                  <th style={{ width: '12%' }}>{isZh ? '用途' : 'Purpose'}</th>
                  <th style={{ width: '14%' }}>{isZh ? '量測頻率' : 'Frequency'}</th>
                  <th style={{ width: '20%' }}>{isZh ? '量測方法' : 'Methods'}</th>
                  <th style={{ width: '10%' }}>{t('common.actions')}</th>
                </tr></thead><tbody>
                  {parameters.map((p, idx) => (
                    <tr key={p.id}>
                      <td>{idx + 1}</td>
                      <td><span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: p.b13_ref ? '#fff7e6' : '#f5f5f5', color: p.b13_ref ? '#e67e22' : '#999' }}>{p.b13_ref ? 'B.13' : isZh ? '手動' : 'Manual'}</span></td>
                      <td>{p.data_parameter && (() => { try { return <InlineMath math={p.data_parameter} />; } catch { return <span>{p.data_parameter}</span>; } })()}</td>
                      <td style={{ fontSize: 13 }}>{p.description || '—'}</td>
                      <td>{p.data_unit && (() => { try { return <InlineMath math={p.data_unit} />; } catch { return <span>{p.data_unit}</span>; } })()}</td>
                      <td style={{ fontSize: 12 }}>
                        {p.purpose_baseline && <span style={{ display: 'inline-block', padding: '1px 6px', background: '#f0f7ff', borderRadius: 3, marginRight: 4, marginBottom: 2 }}>BE</span>}
                        {p.purpose_project && <span style={{ display: 'inline-block', padding: '1px 6px', background: '#f6ffed', borderRadius: 3, marginRight: 4, marginBottom: 2 }}>PE</span>}
                        {p.purpose_leakage && <span style={{ display: 'inline-block', padding: '1px 6px', background: '#fff7e6', borderRadius: 3, marginBottom: 2 }}>LE</span>}
                      </td>
                      <td style={{ fontSize: 12 }} dangerouslySetInnerHTML={{ __html: p.measurement_frequency || '—' }} />
                      <td style={{ fontSize: 12 }} dangerouslySetInnerHTML={{ __html: p.measurement_methods || '—' }} />
                      <td className="actions">{canEdit && <>
                        <button className="btn-edit" onClick={() => openModal(idx)} style={{ marginRight: 8 }}>{t('common.edit')}</button>
                        <button className="btn-delete" onClick={() => handleDeleteParam(idx)}>{t('common.delete')}</button>
                      </>}</td>
                    </tr>
                  ))}
                </tbody></table></div>
              )}

              {canEdit && <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 16 }}>
                <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>{loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}</button>
              </div>}
            </div>
          )}

          {/* Tab 2: 監測報告頻率 */}
          {activeTab === 2 && (
            <div style={cs}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>{isZh ? '15.1 監測報告提交頻率' : '15.1 Frequency of submission of monitoring reports'}</div>
              {canEdit ? <RichTextEditor value={reportingFrequency} onChange={setReportingFrequency} placeholder={isZh ? '請說明監測報告提交頻率...' : 'Describe reporting frequency...'} minHeight="250px" />
                : <div style={{ padding: 16, fontSize: 15, color: '#333', lineHeight: 1.8, minHeight: 250 }} dangerouslySetInnerHTML={{ __html: reportingFrequency || '-' }} />}
              {canEdit && <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 16 }}>
                <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>{loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}</button>
              </div>}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', width: 1200 }}>
            <div className="modal-header">
              <h2>{editingIndex !== null ? (isZh ? '編輯監控參數' : 'Edit') : (isZh ? '新增監控參數' : 'Add')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {/* B.13 下拉 */}
              {editingIndex === null && availableParams.length > 0 && (
                <div style={{ padding: '12px 16px', background: '#fff7e6', borderRadius: 8, border: '1px solid #ffe0b2', marginBottom: 20 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>
                    {isZh ? `從 B.13 選取監控參數（${availableParams.length} 個尚未建立）` : `Select from B.13 (${availableParams.length} remaining)`}
                  </label>
                  <select onChange={e => { if (e.target.value) handleB13Select(e.target.value); }} style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #d0d0d0', borderRadius: 6 }}>
                    <option value="">{isZh ? '不選擇（手動輸入）' : 'Skip (manual)'}</option>
                    {availableParams.map((p, i) => <option key={i} value={p.symbol}>{p.symbol} — {p.source_section} {p.source_label} ({p.para_type === 250 ? 'Ex post' : 'All'})</option>)}
                  </select>
                </div>
              )}

              {modalParam.b13_ref && (
                <div style={{ padding: '10px 16px', background: '#f8faff', borderRadius: 6, border: '1px solid #d0e4ff', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#4A90E2', fontWeight: 600, marginBottom: 4 }}>{modalParam.b13_ref.source_section} — {modalParam.b13_ref.source_label}</div>
                  {modalParam.b13_ref.equation_latex && (() => { try { return <BlockMath math={modalParam.b13_ref.equation_latex} />; } catch { return null; } })()}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                {/* 基本資訊 */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Data/parameter *</label>
                  <input value={modalParam.data_parameter} onChange={e => setModalParam(p => ({ ...p, data_parameter: e.target.value }))} placeholder="e.g. AD_{i,y}^{proj}"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 14, boxSizing: 'border-box' }} />
                  {modalParam.data_parameter && <div style={{ marginTop: 4 }}>{(() => { try { return <InlineMath math={modalParam.data_parameter} />; } catch { return null; } })()}</div>}
                </div>
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Data unit</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={modalParam.data_unit} onChange={e => setModalParam(p => ({ ...p, data_unit: e.target.value }))} placeholder="LaTeX"
                      style={{ flex: 1, padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 13, boxSizing: 'border-box' }} />
                    <select value="" onChange={e => { if (e.target.value) setModalParam(p => ({ ...p, data_unit: e.target.value })); }}
                      style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d0d0d0', borderRadius: 4 }}>
                      <option value="">{isZh ? '常用' : 'Units'}</option>
                      {COMMON_UNITS.map((u, i) => <option key={i} value={u.latex}>{u.label}</option>)}
                    </select>
                  </div>
                  {modalParam.data_unit && <div style={{ marginTop: 4 }}>{(() => { try { return <InlineMath math={modalParam.data_unit} />; } catch { return null; } })()}</div>}
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Description *</label>
                  <input value={modalParam.description} onChange={e => setModalParam(p => ({ ...p, description: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Equations referred</label>
                  <input value={modalParam.equations_referred} onChange={e => setModalParam(p => ({ ...p, equations_referred: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Purpose of data</label>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="checkbox" checked={modalParam.purpose_baseline} onChange={e => setModalParam(p => ({ ...p, purpose_baseline: e.target.checked }))} /> Baseline</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="checkbox" checked={modalParam.purpose_project} onChange={e => setModalParam(p => ({ ...p, purpose_project: e.target.checked }))} /> Project</label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}><input type="checkbox" checked={modalParam.purpose_leakage} onChange={e => setModalParam(p => ({ ...p, purpose_leakage: e.target.checked }))} /> Leakage</label>
                  </div>
                </div>

                {/* 監測專屬欄位 */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? '量測與更新頻率' : 'Measurement and updating frequency'}</label>
                  <RichTextEditor value={modalParam.measurement_frequency} onChange={v => setModalParam(p => ({ ...p, measurement_frequency: v }))} placeholder={isZh ? '量測頻率...' : 'Frequency...'} minHeight="60px" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? '量測方法與程序' : 'Measurement methods and procedures'}</label>
                  <RichTextEditor value={modalParam.measurement_methods} onChange={v => setModalParam(p => ({ ...p, measurement_methods: v }))} placeholder={isZh ? '方法...' : 'Methods...'} minHeight="60px" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? '量測責任單位/人員' : 'Entity/person responsible'}</label>
                  <input value={modalParam.responsible_entity} onChange={e => setModalParam(p => ({ ...p, responsible_entity: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
                </div>

                {/* 量測儀器群組 */}
                <div style={{ gridColumn: '1 / -1', padding: '16px', background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>{isZh ? '量測儀器' : 'Measuring instrument(s)'}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                    <div>
                      <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? '儀器類型' : 'Type of instrument'}</label>
                      <input value={modalParam.instrument_type} onChange={e => setModalParam(p => ({ ...p, instrument_type: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? '精確度等級' : 'Accuracy class'}</label>
                      <input value={modalParam.accuracy_class} onChange={e => setModalParam(p => ({ ...p, accuracy_class: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? '校準要求' : 'Calibration requirements'}</label>
                      <RichTextEditor value={modalParam.calibration_requirements} onChange={v => setModalParam(p => ({ ...p, calibration_requirements: v }))} placeholder={isZh ? '校準程序與頻率...' : 'Calibration procedures...'} minHeight="60px" />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? '位置' : 'Location'}</label>
                      <input value={modalParam.instrument_location} onChange={e => setModalParam(p => ({ ...p, instrument_location: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>{isZh ? 'QA/QC 程序' : 'QA/QC procedures'}</label>
                  <RichTextEditor value={modalParam.qaqc_procedures} onChange={v => setModalParam(p => ({ ...p, qaqc_procedures: v }))} placeholder={isZh ? 'QA/QC...' : 'QA/QC...'} minHeight="60px" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Treatment of uncertainty</label>
                  <RichTextEditor value={modalParam.treatment_of_uncertainty} onChange={v => setModalParam(p => ({ ...p, treatment_of_uncertainty: v }))} placeholder={isZh ? '不確定性處理...' : 'Uncertainty...'} minHeight="60px" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Additional comments</label>
                  <RichTextEditor value={modalParam.additional_comments} onChange={v => setModalParam(p => ({ ...p, additional_comments: v }))} placeholder={isZh ? '補充（選填）...' : 'Optional...'} minHeight="50px" />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              {editingIndex === null && <button className="btn-secondary" onClick={() => handleSaveParam(true)} style={{ marginLeft: 8 }}>{isZh ? '儲存並繼續' : 'Save & Next'}</button>}
              <button className="btn-primary" onClick={() => handleSaveParam(false)} style={{ marginLeft: 8 }}>{editingIndex !== null ? t('common.save') : (isZh ? '儲存並關閉' : 'Save & Close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB15Page;
